import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Camera, Volume2, GraduationCap, BookOpen } from "lucide-react";
import { logFunnel, trackEvent } from "@/lib/analytics";
import { renderSummary } from "@/lib/renderSummary";
import { INSTANT_EXAMPLES } from "@/lib/instantExamples";

const TRY_URL = "/?format=revision_dys";
const EX = INSTANT_EXAMPLES[0];
const DYS = EX.variants.find((v) => v.settings.format === "revision_dys");

const FEATURES = [
  "Phrases courtes, une seule idée par ligne",
  "Les mots difficiles expliqués simplement",
  "Les erreurs à éviter, pour ne pas confondre",
  "« Je me teste » : 5 questions avec les réponses",
  "Adaptée à sa classe : primaire, collège ou lycée",
];

const EXTRAS = [
  { icon: Camera, text: "Prenez la leçon en photo, pas besoin de la recopier" },
  { icon: Volume2, text: "Votre enfant peut écouter la fiche à voix haute" },
  { icon: GraduationCap, text: "Prête en 5 secondes, pour les devoirs du soir" },
];

const Cta = ({ testid }) => (
  <Link to={TRY_URL} data-testid={testid} onClick={() => trackEvent("dys_cta_click")}
    className="inline-flex items-center gap-3 bg-[#002FA7] text-white px-7 py-4 font-black tracking-tight hover:bg-[#00237d] transition-colors" style={{ fontFamily: "Outfit" }}>
    Essayer avec sa leçon <ArrowRight className="w-4 h-4" />
  </Link>
);

const BeforeAfter = () => (
  <div className="grid md:grid-cols-2 border-2 border-[#002FA7]" data-testid="dys-before-after">
    <div className="p-6 md:border-r border-border">
      <div className="sux-marker mb-3">Avant · la leçon du cahier</div>
      <p className="text-[13px] leading-relaxed text-muted-foreground">{EX.source[0]}</p>
    </div>
    <div className="p-6 bg-[#002FA7]/[0.04]">
      <div className="sux-marker mb-3 text-[#002FA7]">Après · la fiche DYS de SUX1 (niveau collège)</div>
      <div className="summary-content notranslate text-[14px] md:max-h-[460px] md:overflow-y-auto pr-2" dangerouslySetInnerHTML={{ __html: renderSummary(DYS.summary[0]) }} />
    </div>
  </div>
);

export default function Dys() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Fiche de révision DYS en 5 secondes · SUX1, l'aide aux devoirs pour enfants dyslexiques";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    meta?.setAttribute("content", "Votre enfant est dyslexique ou DYS ? Collez ou photographiez sa leçon : SUX1 en fait une fiche de révision courte, aérée et adaptée à sa classe. Gratuit pour essayer, sans abonnement.");
    logFunnel("dys_view");
    return () => { document.title = prev; if (meta && prevDesc) meta.setAttribute("content", prevDesc); };
  }, []);

  return (
    <div className="min-h-screen bg-card" data-testid="dys-page">
      <section className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 lg:py-24">
          <div className="sux-marker mb-4">Pour les parents d'enfants DYS · dyslexie · dyspraxie · TDAH</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] mb-6" style={{ fontFamily: "Outfit" }}>
            Ses leçons,<br /><span className="text-[#002FA7]">enfin faciles à lire.</span>
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed mb-8">
            Collez la leçon ou prenez-la en photo. SUX1 en fait une <b className="text-foreground">fiche de révision DYS</b> : courte, aérée, avec des mots simples et adaptée à sa classe. En 5 secondes.
          </p>
          <Cta testid="dys-cta-top" />
          <p className="mt-3 text-sm text-muted-foreground">Exemple gratuit ci-dessous. Pour ses leçons : dès 2 € les 20 fiches, sans abonnement.</p>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-14">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-6" style={{ fontFamily: "Outfit" }}>Un vrai exemple, sans retouche</h2>
          <BeforeAfter />
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-14 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-5" style={{ fontFamily: "Outfit" }}>Ce que contient la fiche</h2>
            <ul className="space-y-3">
              {FEATURES.map((f) => <li key={f} className="flex gap-3"><Check className="w-5 h-5 text-[#002FA7] shrink-0" />{f}</li>)}
            </ul>
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-5" style={{ fontFamily: "Outfit" }}>Pensé pour le soir</h2>
            <ul className="space-y-3">
              {EXTRAS.map(({ icon: I, text }) => <li key={text} className="flex gap-3"><I className="w-5 h-5 text-[#002FA7] shrink-0" />{text}</li>)}
            </ul>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-3" style={{ fontFamily: "Outfit" }}>Essayez ce soir avec sa vraie leçon.</h2>
          <p className="text-muted-foreground mb-7 max-w-xl">Le format DYS est déjà sélectionné. Dès 2 € les 20 fiches, sans abonnement, crédits sans date limite.</p>
          <Cta testid="dys-cta-bottom" />
          <p className="mt-8 text-sm text-muted-foreground flex items-center gap-2"><BookOpen className="w-4 h-4" />
            SUX1 est un outil d'aide à la révision : il ne remplace ni l'orthophoniste ni l'enseignant.
          </p>
        </div>
      </section>
    </div>
  );
}
